import { EXC_DTC_FILES_V1_FILE_REQUEST_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/exc-dtc-files-v1-file-request-state';
import { EXC_DTC_FILES_V1_FILE_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/exc-dtc-files-v1-file-state';
import { EXC_JPMAM_V1_JPMAM_ORDER_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/exc-jpmam-v1-jpmam-order-state';
import { EXC_JPMAM_V1_NET_ORDER_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/exc-jpmam-v1-net-order-state';
import { EXC_ROUTE_V1_FIX_ORDER_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/exc-route-v1-fix-order-state';
import { EXC_ROUTE_V1_FIX_SESSION_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/exc-route-v1-fix-session-state';
import { IXC_CORPACTIONS_CAADMIN_V1_ACTION_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-corpactions-caadmin-v1-action-state';
import { IXC_DTCC_CNS_V1_ASSET_DAY_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-dtcc-cns-v1-asset-day-state';
import { IXC_DTCC_DTC_V1_ASSET_DAY_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-dtcc-dtc-v1-asset-day-state';
import { IXC_ORDERS_V1_ORDER_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-orders-v1-order-state';
import { IXC_PAYMENTS_V1_ACH_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-payments-v1-ach-state';
import { IXC_SWEEPS_CLEARING_V1_SWEEP_ORDER_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-sweeps-clearing-v1-sweep-order-state';
import { IXC_SWEEPS_CLEARING_V1_SWEEP_PARTICIPANT_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-sweeps-clearing-v1-sweep-participant-state';
import { IXC_TRADE_V1_TRADE_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-trade-v1-trade-state';
import { IXC_TRF_V1_TRF_SESSION_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixc-trf-v1-trf-session-state';
import { IXF_WATCHLIST_V1_FINCEN314_MATCH_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixf-watchlist-v1-fincen314-match-state';
import { IXM_TRIGGER_V1_TRIGGER_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixm-trigger-v1-trigger-state';
import { IXM_USERAUTH_V1_ORGANIZATION_MEMBER_INVITATION_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixm-userauth-v1-organization-member-invitation-state';
import { IXT_E2E_V1_TEST_RUN_STATE_ENTITY_NAME } from '@interxfi/api-client/generated/entities/ixt-e2e-v1-test-run-state';
import type { PillVariant } from '@interxfi/design-system/components/pill/pill';
import { ENTITY_STATUS_VARIANTS as baseVariants } from '../generated/entity-status-variants';

export type EntityName = keyof typeof baseVariants;
type EntityStatus<TEntity extends EntityName> = keyof (typeof baseVariants)[TEntity];

// This should be regarded as the source of truth for entity status pill variants
export const ENTITY_STATUS_VARIANTS: {
  [K in EntityName]: {
    [S in keyof (typeof baseVariants)[K]]: PillVariant;
  };
} = {
  ...baseVariants,
  [IXC_ORDERS_V1_ORDER_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_ORDERS_V1_ORDER_STATE_ENTITY_NAME],
    ACTIVE: 'neutral',
  },
  [IXC_PAYMENTS_V1_ACH_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_PAYMENTS_V1_ACH_STATE_ENTITY_NAME],
    RETURNED: 'dark',
  },
  [IXC_TRADE_V1_TRADE_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_TRADE_V1_TRADE_STATE_ENTITY_NAME],
    MATCHED: 'primary',
    NETTED: 'primary',
    ROUTE_ONLY: 'secondary',
    UTC_ONLY: 'tertiary',
  },
  [IXM_TRIGGER_V1_TRIGGER_STATE_ENTITY_NAME]: {
    ...baseVariants[IXM_TRIGGER_V1_TRIGGER_STATE_ENTITY_NAME],
    PAUSED: 'dark',
  },
  [IXM_USERAUTH_V1_ORGANIZATION_MEMBER_INVITATION_STATE_ENTITY_NAME]: {
    ...baseVariants[IXM_USERAUTH_V1_ORGANIZATION_MEMBER_INVITATION_STATE_ENTITY_NAME],
    ACCEPTED: 'green',
  },
  [EXC_ROUTE_V1_FIX_ORDER_STATE_ENTITY_NAME]: {
    ...baseVariants[EXC_ROUTE_V1_FIX_ORDER_STATE_ENTITY_NAME],
    ACTIVE_C: 'danger',
  },
  [EXC_JPMAM_V1_JPMAM_ORDER_STATE_ENTITY_NAME]: {
    ...baseVariants[EXC_JPMAM_V1_JPMAM_ORDER_STATE_ENTITY_NAME],
    POSTED: 'neutral',
  },
  [EXC_JPMAM_V1_NET_ORDER_STATE_ENTITY_NAME]: {
    ...baseVariants[EXC_JPMAM_V1_NET_ORDER_STATE_ENTITY_NAME],
    CONFIRMED: 'primary',
    POSTED: 'neutral',
  },
  [IXC_SWEEPS_CLEARING_V1_SWEEP_ORDER_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_SWEEPS_CLEARING_V1_SWEEP_ORDER_STATE_ENTITY_NAME],
    POSTED: 'neutral',
  },
  [IXC_SWEEPS_CLEARING_V1_SWEEP_PARTICIPANT_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_SWEEPS_CLEARING_V1_SWEEP_PARTICIPANT_STATE_ENTITY_NAME],
    SWEEPING: 'primary',
    NOT_SWEEPING: 'danger',
  },
  [IXT_E2E_V1_TEST_RUN_STATE_ENTITY_NAME]: {
    ...baseVariants[IXT_E2E_V1_TEST_RUN_STATE_ENTITY_NAME],
    SUCCESS: 'green',
    FAILURE: 'danger',
  },
  [IXC_TRF_V1_TRF_SESSION_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_TRF_V1_TRF_SESSION_STATE_ENTITY_NAME],
    LOGGED_ON: 'green',
    DISCONNECTED: 'danger',
    STOPPED: 'danger',
  },
  [EXC_ROUTE_V1_FIX_SESSION_STATE_ENTITY_NAME]: {
    ...baseVariants[EXC_ROUTE_V1_FIX_SESSION_STATE_ENTITY_NAME],
    LOGGED_ON: 'green',
    DISCONNECTED: 'danger',
    STOPPED: 'danger',
  },
  [IXC_CORPACTIONS_CAADMIN_V1_ACTION_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_CORPACTIONS_CAADMIN_V1_ACTION_STATE_ENTITY_NAME],
    COMPLETE: 'primary',
    PARTIAL: 'secondary',
    CANCELED: 'danger',
  },
  [EXC_DTC_FILES_V1_FILE_REQUEST_STATE_ENTITY_NAME]: {
    ...baseVariants[EXC_DTC_FILES_V1_FILE_REQUEST_STATE_ENTITY_NAME],
    FULFILLED: 'primary',
  },
  [EXC_DTC_FILES_V1_FILE_STATE_ENTITY_NAME]: {
    ...baseVariants[EXC_DTC_FILES_V1_FILE_STATE_ENTITY_NAME],
    VALIDATED: 'primary',
    INVALID: 'danger',
  },
  [IXC_DTCC_CNS_V1_ASSET_DAY_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_DTCC_CNS_V1_ASSET_DAY_STATE_ENTITY_NAME],
    BREAK: 'danger',
    BLOCKED: 'danger',
    RECONCILED: 'primary',
  },
  [IXF_WATCHLIST_V1_FINCEN314_MATCH_STATE_ENTITY_NAME]: {
    ...baseVariants[IXF_WATCHLIST_V1_FINCEN314_MATCH_STATE_ENTITY_NAME],
    OPENED: 'primary',
  },
  [IXC_DTCC_DTC_V1_ASSET_DAY_STATE_ENTITY_NAME]: {
    ...baseVariants[IXC_DTCC_DTC_V1_ASSET_DAY_STATE_ENTITY_NAME],
    BREAK: 'danger',
    BLOCKED: 'dark',
    RECONCILED: 'green',
    CURRENT: 'primary',
  },
};

export function getEntityVariant<TEntity extends EntityName>(entityName: TEntity, variantOption: EntityStatus<TEntity> | undefined): PillVariant {
  return ENTITY_STATUS_VARIANTS[entityName]?.[variantOption ?? 'UNSPECIFIED'] ?? 'neutral';
}
